import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import { useTheme } from "@material-ui/core/styles";
import api from "../../services/api";
import useWhatsApps from "../../hooks/useWhatsApps";
import usePlans from "../../hooks/usePlans";
import WebphoneFab from "../../components/WebphoneFab";
import { AuthContext } from "../Auth/AuthContext";

const WAVOIP_CDN = "https://cdn.jsdelivr.net/npm/@wavoip/wavoip-webphone/dist/index.umd.min.js";

const WhatsAppsContext = createContext();

const WhatsAppsProvider = ({ children }) => {
  const theme = useTheme();
  const whatsAppData = useWhatsApps();
  const { loading = false, whatsApps = [] } = whatsAppData || {};
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const wavoipRenderDoneRef = useRef(false);

  const [wavoipToken, setWavoipToken] = useState(null);
  const [wavoipEnabled, setWavoipEnabled] = useState(false);
  const [loadingSession, setLoadingSession] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPlanConfig = async () => {
      try {
        if (user?.companyId) {
          const planConfigs = await getPlanCompany(undefined, user.companyId);
          setWavoipEnabled(planConfigs?.plan?.wavoip || false);
        }
      } catch (err) {
        console.error("Erro ao buscar configuração do plano:", err);
        setWavoipEnabled(false);
      }
    };
    fetchPlanConfig();
  }, [user?.companyId, getPlanCompany]);

  useEffect(() => {
    // ✅ CORREÇÃO: Só fazer requisição se o usuário estiver autenticado
    if (!user?.id || !user?.companyId) {
      setLoadingSession(false);
      return;
    }

    const fetchSession = async () => {
      try {
        const { data } = await api.get("/call/historical/user/whatsapp");

        // let wavoipToken  = "";
        // for(const d of data){
        //   if(d?.wavoip){
        //     wavoipToken = d.wavoip;
        //     break;
        //   }
        // }
        console.log('whavoip token', data)
        setWavoipToken(data?.whatsapp?.wavoip || null);
      } catch (err) {
        // ✅ CORREÇÃO: Não logar erro se for 401 (não autenticado)
        if (err?.response?.status !== 401) {
          console.error("Erro fetchSession:", err);
        }
        setWavoipToken(null);
      } finally {
        setLoadingSession(false);
      }
    };
    fetchSession();
  }, [user?.id, user?.companyId]);

  /**
   * Carrega o widget oficial WAVoIP (window.wavoip) logo após o login quando o plano
   * tem wavoip habilitado. Assim o sistema pode receber chamadas e tocar mesmo com
   * o widget fechado; o usuário abre pelo FAB quando quiser.
   */
  // 1) Assim que o usuário está logado e o plano tem wavoip, carrega o script e faz render()
  useEffect(() => {
    if (!user?.id || !wavoipEnabled) return;

    const loadAndRenderWidget = async () => {
      if (!window.wavoipWebphone) {
        await new Promise((resolve, reject) => {
          if (document.querySelector(`script[src="${WAVOIP_CDN}"]`)) {
            if (window.wavoipWebphone) return resolve();
            const check = setInterval(() => {
              if (window.wavoipWebphone) {
                clearInterval(check);
                resolve();
              }
            }, 100);
            return;
          }
          const script = document.createElement("script");
          script.src = WAVOIP_CDN;
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error("Falha ao carregar widget WAVoIP"));
          document.head.appendChild(script);
        });
      }
      if (!wavoipRenderDoneRef.current && window.wavoipWebphone) {
        await window.wavoipWebphone.render({
          position: "bottom-right",
          settingsMenu: {
            showSettingsButton: false,
            showWidgetButton: false,
          },
        });
        wavoipRenderDoneRef.current = true;
      }
      if (window.wavoip) {
        window.wavoip.settings.setShowSettings(false);
        window.wavoip.settings.setShowWidgetButton(false);
        const mode = theme?.palette?.type ?? theme?.palette?.mode ?? "light";
        window.wavoip.theme.setTheme(mode === "dark" ? "dark" : "light");
      }
    };

    loadAndRenderWidget().catch((err) =>
      console.error("Erro ao carregar widget WAVoIP:", err)
    );
  }, [user?.id, wavoipEnabled]);

  // 2) Assim que o token estiver disponível, registra o dispositivo para receber chamadas (tocar)
  useEffect(() => {
    if (!wavoipToken) return;
    if (!window.wavoip) {
      const retry = setInterval(() => {
        if (window.wavoip) {
          clearInterval(retry);
          window.wavoip.settings.setShowSettings(false);
          window.wavoip.device.addDevice(wavoipToken);
          window.wavoip.settings.setShowWidgetButton(false);
        }
      }, 200);
      return () => clearInterval(retry);
    }
    window.wavoip.settings.setShowSettings(false);
    window.wavoip.device.addDevice(wavoipToken);
    window.wavoip.settings.setShowWidgetButton(false);
  }, [wavoipToken]);

  // Log error state for debugging
  if (error) {
    console.warn("WhatsAppsProvider error:", error);
  }

  return (
    <WhatsAppsContext.Provider value={{ whatsApps, loading, error }}>
      {children}
      {wavoipEnabled && wavoipToken && <WebphoneFab />}
    </WhatsAppsContext.Provider>
  );
};

export { WhatsAppsContext, WhatsAppsProvider };